import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { colors } from '../../tokens/colors';
import { borderRadius } from '../../tokens/spacing';

/**
 * Drawer contextual.
 *
 * O DESIGN.md do "Core Enterprise Logic" prescreve `context-drawer: 400px` e diz
 * explicitamente para **usar drawer em vez de navegar para outra página** quando
 * o usuário precisa de detalhe sem perder o contexto da lista. As 128 telas do
 * protótipo usam esse padrão 21 vezes.
 *
 * Não existia no design system: quatro páginas (`StockPage`, `PosSalePage`,
 * `ProcurementRequestsPage`, `SupportTicketsPage`) implementavam drawer ad-hoc,
 * cada uma do seu jeito, com largura e comportamento de fechamento diferentes.
 *
 * Fica contido na área de conteúdo quando o ancestral estabelece bloco de
 * contenção (o shell aplica `transform: translateZ(0)` no outlet), de modo que o
 * drawer não cobre o rail de navegação.
 */

export interface DrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  /** Ações fixas no pé do drawer (salvar, cancelar). */
  readonly footer?: ReactNode;
  /** `context` = 400px (padrão da spec). `wide` = 560px para tabelas internas. */
  readonly size?: 'context' | 'wide';
  readonly className?: string;
}

const WIDTHS = { context: '400px', wide: '560px' } as const;

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'context',
  className,
}: DrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // move o foco para o painel ao abrir, para leitor de tela e teclado
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className={cn('ds-drawer', className)} style={{ position: 'absolute', inset: 0, zIndex: 40 }}>
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="ds-drawer__scrim"
        style={{
          position: 'absolute',
          inset: 0,
          border: 0,
          cursor: 'default',
          background: colors.surface.overlay,
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="ds-drawer__panel"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: WIDTHS[size],
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: colors.surface.card,
          borderLeft: `1px solid ${colors.border.default}`,
          outline: 'none',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            padding: '1rem',
            borderBottom: `1px solid ${colors.border.default}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                margin: 0,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                fontWeight: 600,
                color: colors.text.primary,
              }}
            >
              {title}
            </h2>
            {description ? (
              <p
                style={{
                  margin: '0.125rem 0 0',
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  color: colors.text.secondary,
                }}
              >
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              flexShrink: 0,
              width: '2rem',
              height: '2rem',
              border: 0,
              background: 'none',
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              color: colors.text.secondary,
              fontSize: '1.125rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1rem' }}>{children}</div>

        {footer ? (
          <footer
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              borderTop: `1px solid ${colors.border.default}`,
              background: colors.surface.page,
            }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
